/**
 * Category rail — progressive enhancement.
 *
 * The rail renders and navigates without JavaScript: every row is a real link
 * and capped categories render their first six. This file adds the two
 * behaviours that need scripting:
 *
 *   1. "Show all N" reveals the hidden rows in place, with no page load.
 *   2. On mobile the tree renders collapsed; tapping a category row expands it
 *      instead of navigating.
 *
 * Everything is delegated from the document so it survives the section
 * re-render that Horizon performs after each filter change.
 */
(function () {
  if (window.__categoryRailBound) return;
  window.__categoryRailBound = true;

  var MOBILE_QUERY = '(max-width: 749px)';
  var SUB_PARAM = 'filter.p.t.category';
  var CATEGORY_PARAM = 'filter.p.m.custom.hecho_category';
  var STYLE_PARAM = 'filter.p.product_type';

  /* Column scroll position, saved across the section re-render a filter
     triggers, so the rail doesn't snap back to the top after every click. */
  var savedColumnScroll = null;

  /* Which categories the buyer has expanded with "Show all". Every filter click
     re-renders the rail from scratch, so without this the list would snap back
     to six rows the moment they picked one of the rows they just revealed.
     Kept in sessionStorage so it also survives a real page load. */
  var EXPANDED_KEY = 'hecho:category-rail:expanded';

  function readExpanded() {
    try {
      var raw = sessionStorage.getItem(EXPANDED_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function writeExpanded(keys) {
    try {
      sessionStorage.setItem(EXPANDED_KEY, JSON.stringify(keys));
    } catch (e) {
      /* Private browsing and blocked storage just lose the memory; the rail
         still works, it simply re-collapses. */
    }
  }

  function rememberExpanded(key) {
    if (!key) return;
    var keys = readExpanded();
    if (keys.indexOf(key) === -1) {
      keys.push(key);
      writeExpanded(keys);
    }
  }

  /* Direct children only. The style list is nested inside the subcategory list,
     so an unscoped query would reveal a category's hidden styles too whenever
     its own "Show all" was expanded. */
  function revealOverflow(list) {
    if (!list) return;
    var hiddenRows = list.querySelectorAll(':scope > .category-rail__child--overflow');
    for (var i = 0; i < hiddenRows.length; i++) hiddenRows[i].hidden = false;
    var item = list.querySelector(':scope > .category-rail__child--show-all');
    if (item) item.hidden = true;
  }

  /**
   * The list a remembered group should re-reveal.
   *
   * Three shapes carry [data-category-rail-group]: a category <li> holding a
   * .category-rail__children list, a Style group <li> holding a
   * .category-rail__styles list, and the flat style <ul> which IS the list.
   * Missing the styles case is how an expanded Style group silently snapped
   * back to six rows on the next filter click - the restore ran, found no
   * list, and did nothing.
   */
  function listForGroup(group) {
    if (!group) return null;
    if (group.matches('.category-rail__styles, .category-rail__children')) return group;
    return group.querySelector(':scope > .category-rail__children, :scope > .category-rail__styles');
  }

  /* Re-open whatever was open before the re-render. */
  function applyExpandedState(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var keys = readExpanded();
    if (!keys.length) return;
    var groups = scope.querySelectorAll('[data-category-rail-group][data-category-key]');
    for (var i = 0; i < groups.length; i++) {
      if (keys.indexOf(groups[i].getAttribute('data-category-key')) !== -1) {
        revealOverflow(listForGroup(groups[i]));
      }
    }
  }

  function filterColumn(node) {
    return node ? node.closest('.facets-block-wrapper--vertical') : null;
  }

  function uncheckAll(form, name) {
    var inputs = form.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === name && inputs[i].checked) inputs[i].checked = false;
    }
  }

  function findInput(form, name, value) {
    var inputs = form.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === name && inputs[i].value === value) return inputs[i];
    }
    return null;
  }

  /**
   * A rail row is a real link so it works without JavaScript, but following it
   * is a full page load — which is what threw the page back to the top on every
   * click. With JavaScript we instead tick the matching checkbox in the theme's
   * own facet form and let its component do the in-place update, exactly as
   * Availability, Color and Brand already do.
   */
  function applyFilterFromRow(row, event) {
    var name = row.getAttribute('data-filter-name');
    var value = row.getAttribute('data-filter-value');
    if (!name || value === null) return;

    var form = row.closest('form');
    if (!form) return;

    var input = findInput(form, name, value);
    /* No matching input means the source groups aren't in the DOM; let the link
       navigate rather than silently doing nothing. */
    if (!input) return;

    event.preventDefault();

    var wasActive = row.classList.contains('is-active');

    /**
     * Clicking a row that is on switches it off. Always, at every level.
     *
     * This used to have an exception: clicking a selected subcategory while
     * another was also selected narrowed to the one clicked instead of
     * clearing it, on the reasoning that selecting a second subcategory takes
     * the nested styles away - the style values are then the union across both
     * and cannot be attributed to either - so re-clicking one was the way back
     * to its styles.
     *
     * That made one gesture mean two different things depending on how many
     * rows happened to be on, and the wrong one far more often: with
     * Sweatshirts and T-Shirts both selected, clicking Sweatshirts to drop it
     * dropped T-Shirts instead and left Sweatshirts standing. Nothing on
     * screen could have told you which it would be.
     *
     * Plain toggle reaches the same places anyway - to get back to one
     * subcategory's styles, switch the other one off - and it is the one
     * reading of a click that never has to be guessed at.
     */

    /* Clearing a category clears its subcategories too, per the rail spec. */
    if (wasActive && name === CATEGORY_PARAM) {
      uncheckAll(form, SUB_PARAM);
    }

    /**
     * Any move at the category or subcategory level clears the Style filter.
     *
     * A style belongs to one subcategory, so keeping it alive while the level
     * above it changes is never what is meant - and it does real damage,
     * because a style zeroes the count of every sibling subcategory, which
     * then drops out of the rail entirely. Pick Apparel, Sweatshirts, then
     * Embroidered Sweatshirts, and T-Shirts vanishes with no way back:
     * toggling Apparel off clears the subcategory but leaves the style, so
     * everything stays narrowed to sweatshirts however many times you click.
     * The only escape was un-picking the style, which nothing on screen
     * suggested.
     */
    if (name === CATEGORY_PARAM || name === SUB_PARAM) {
      uncheckAll(form, STYLE_PARAM);
    }

    var column = filterColumn(row);
    savedColumnScroll = column ? column.scrollTop : null;

    input.checked = !wasActive;

    input.dispatchEvent(new Event('change', { bubbles: true }));

    /* The re-render is asynchronous and the mutation hook can be early, so
       drive the restore from this side too. */
    scheduleSync();
  }

  /**
   * A Style group row stands for every style inside it. Style values share one
   * filter parameter, so ticking them together reads as "or" - clicking
   * Handbags shows every bag style at once, which the rail has no other way to
   * express. Clicking it again when they are all on clears the group.
   */
  function applyGroupSelect(button) {
    var group = button.closest('.category-rail__style-group');
    if (!group) return;

    var form = button.closest('form');
    if (!form) return;

    var rows = group.querySelectorAll('.category-rail__row--style[data-filter-name][data-filter-value]');
    var inputs = [];
    for (var i = 0; i < rows.length; i++) {
      var input = findInput(form, rows[i].getAttribute('data-filter-name'), rows[i].getAttribute('data-filter-value'));
      if (input) inputs.push(input);
    }
    /* No inputs means the source groups aren't in the DOM. There is no link to
       fall back to on a group row, so do nothing rather than half-apply. */
    if (!inputs.length) return;

    var allOn = true;
    for (var j = 0; j < inputs.length; j++) {
      if (!inputs[j].checked) {
        allOn = false;
        break;
      }
    }

    var column = filterColumn(button);
    savedColumnScroll = column ? column.scrollTop : null;

    for (var k = 0; k < inputs.length; k++) inputs[k].checked = !allOn;

    /* One change event for the whole group, not one per input. The theme
       serialises the entire form on change, so a single event already carries
       every box we just ticked - firing one each would kick off a section
       re-render per style and throw all but the last away. */
    inputs[inputs.length - 1].dispatchEvent(new Event('change', { bubbles: true }));

    scheduleSync();
  }

  /* ---- subcategories Shopify has stopped reporting ---------------------- */

  var STALE_CLASS = 'category-rail__stale';

  /**
   * A subcategory can stay in the URL while Shopify stops honouring it: pick a
   * Style that no product in that subcategory has, and its count falls to zero,
   * at which point it vanishes from the filter values, the active values, the
   * form and its own filter chip. The parameter is still there and still
   * widening both the result set and the Style list, but nothing on screen says
   * so, and there is no control left to switch it off.
   *
   * The server render cannot see this - by the time Liquid runs, the value is
   * simply absent - so the row is rebuilt here from the URL plus the menu map
   * the snippet emits.
   */
  function urlSubValues() {
    try {
      return new URLSearchParams(window.location.search).getAll(SUB_PARAM);
    } catch (e) {
      return [];
    }
  }

  function railMap(root) {
    var el = root.querySelector('[data-category-rail-map]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /* Matched by reading attributes rather than by building a selector, so a
     taxonomy id never has to be escaped into one. */
  function hasRowFor(root, value) {
    var rows = root.querySelectorAll('[data-filter-name][data-filter-value]');
    for (var i = 0; i < rows.length; i++) {
      if (
        rows[i].getAttribute('data-filter-name') === SUB_PARAM &&
        rows[i].getAttribute('data-filter-value') === value
      ) {
        return true;
      }
    }
    return false;
  }

  function findByKey(root, key) {
    var nodes = root.querySelectorAll('[data-category-key]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-category-key') === key) return nodes[i];
    }
    return null;
  }

  function staleRow(entry, value) {
    var li = document.createElement('li');
    li.className = 'category-rail__child ' + STALE_CLASS;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'category-rail__row category-rail__row--sub category-rail__row--stale is-active';
    button.setAttribute('data-category-rail-stale', value);
    button.setAttribute('aria-pressed', 'true');
    button.title = entry.label + ' is still selected, but nothing matches it alongside the other filters. Click to remove it.';

    var label = document.createElement('span');
    label.className = 'category-rail__label';
    label.textContent = entry.label;

    var count = document.createElement('span');
    count.className = 'category-rail__count';
    count.setAttribute('aria-hidden', 'true');
    count.textContent = '0';

    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = ', selected, no matches with the current filters';

    button.appendChild(label);
    button.appendChild(count);
    button.appendChild(sr);
    li.appendChild(button);
    return li;
  }

  function renderStaleSubs(root) {
    /* Cleared and rebuilt each pass rather than reconciled - the sync ladder
       runs this several times per filter change, and rebuilding is cheap. */
    var existing = root.querySelectorAll('.' + STALE_CLASS);
    for (var i = 0; i < existing.length; i++) existing[i].remove();

    var values = urlSubValues();
    if (!values.length) return;

    var map = railMap(root);
    if (!map.length) return;

    for (var j = 0; j < values.length; j++) {
      var value = values[j];
      if (hasRowFor(root, value)) continue;

      var entry = null;
      for (var k = 0; k < map.length; k++) {
        if (map[k].id === value) {
          entry = map[k];
          break;
        }
      }
      if (!entry) continue;

      var group = findByKey(root, entry.group);
      var list = group ? group.querySelector(':scope > .category-rail__children') : null;
      /* Its category is not on screen either - better to show nothing than to
         invent a group heading that the server render never produced. */
      if (!list) continue;

      list.appendChild(staleRow(entry, value));
    }
  }

  /**
   * Removing one is a URL edit, not a checkbox: there is no input left to
   * untick. `updateFiltersByURL` is the theme's own path for exactly this - it
   * is what the filter chips use - so this stays an in-place update rather than
   * a page load.
   */
  function removeStaleSub(button) {
    var value = button.getAttribute('data-category-rail-stale');
    if (!value) return;

    var url = new URL(window.location.href);
    var kept = url.searchParams.getAll(SUB_PARAM).filter(function (v) {
      return v !== value;
    });
    url.searchParams.delete(SUB_PARAM);
    kept.forEach(function (v) {
      url.searchParams.append(SUB_PARAM, v);
    });

    var column = filterColumn(button);
    savedColumnScroll = column ? column.scrollTop : null;

    var host = button.closest('facets-form-component');
    if (host && typeof host.updateFiltersByURL === 'function') {
      host.updateFiltersByURL(url.toString());
      scheduleSync();
    } else {
      window.location.href = url.toString();
    }
  }

  /* ---- heading for the brand band --------------------------------------- */

  var BAND_SELECTOR = '.shopify-section:has(img):has(+ .shopify-section #SearchWithFilters)';
  var BAND_HEADING = 'Shop Our Brands';

  /**
   * The band around the brand tiles is styling and belongs in CSS, but its
   * heading is content and really belongs to the section - a heading block
   * added to it in the theme editor would be the proper home. It is written
   * here because that section has no heading of its own and adding one means
   * editing the template JSON of every collection that uses it.
   *
   * The trade, stated plainly: this is a real element with real text, so
   * screen readers announce it, but it is not in the HTML the server sends, so
   * search engines will not see it. The moment a heading block exists on the
   * section, delete this and its stylesheet rule.
   */
  function addBandHeading() {
    var bands;
    try {
      bands = document.querySelectorAll(BAND_SELECTOR);
    } catch (e) {
      /* No :has() support, which also means no band to head. */
      return;
    }

    for (var i = 0; i < bands.length; i++) {
      var inner = bands[i].querySelector('.section') || bands[i].firstElementChild;
      if (!inner) continue;
      if (inner.querySelector(':scope > .category-rail__band-heading')) continue;

      /* The Style Guide page shares this band and already has a heading of its
         own ("Explore Our Style Guide"). Adding a second one above it read as
         a mistake, so a section that already says what it is keeps saying it. */
      if (inner.querySelector('h1, h2, h3, h4')) continue;

      var heading = document.createElement('h2');
      heading.className = 'category-rail__band-heading';
      heading.textContent = BAND_HEADING;
      inner.insertBefore(heading, inner.firstChild);
    }
  }

  /* ---- brand tiles as cards ---------------------------------------------- */

  var CARD_CLASS = 'hecho-brand-card';

  /**
   * Last path segment of a link's href, which for these links is the
   * collection handle. Query string and trailing slash stripped, because the
   * rail's own brand links can carry filter params.
   */
  function collectionHandle(node) {
    var href = node.getAttribute('href') || '';
    href = href.split('?')[0].split('#')[0].replace(/\/+$/, '');
    var parts = href.split('/');
    return parts[parts.length - 1] || '';
  }

  /**
   * Product count per brand, read off the Brands rail that is already on the
   * page. Keyed by collection handle rather than by the brand's name: the two
   * are written differently in different places ("BelJoy" against "Beljoy"),
   * and the handle is the one thing both the rail link and the tile link
   * agree on exactly.
   */
  function brandCounts() {
    var map = {};
    var rows = document.querySelectorAll('[data-brand-rail] a.category-rail__row');
    for (var i = 0; i < rows.length; i++) {
      var count = rows[i].querySelector('.category-rail__count');
      if (!count) continue;
      var handle = collectionHandle(rows[i]);
      if (handle) map[handle] = count.textContent.trim();
    }
    return map;
  }

  /**
   * Turns each brand tile into a card: the caption moves onto the bottom of
   * the image over a scrim, and the brand's product count is added under its
   * name.
   *
   * Done here rather than in the section because the tiles are hand-built
   * group blocks - an image block and a link block sitting side by side, with
   * no product count anywhere in the markup - and the same arrangement is
   * repeated across sixteen collection templates. The count is not fetched;
   * it is lifted from the Brands rail a few hundred pixels away, which
   * already has the number and keeps it correct for free.
   *
   * Idempotent, because syncRail runs this many times per page: the name is
   * only wrapped if it has not been wrapped already, and the count element is
   * reused rather than appended again.
   */
  function decorateBrandCards() {
    var bands;
    try {
      bands = document.querySelectorAll(BAND_SELECTOR);
    } catch (e) {
      /* No :has() support, which also means no band to decorate. */
      return;
    }
    if (!bands.length) return;

    var counts = brandCounts();

    for (var i = 0; i < bands.length; i++) {
      var tiles = bands[i].querySelectorAll('.group-block-content');

      for (var j = 0; j < tiles.length; j++) {
        var content = tiles[j];
        var image = content.querySelector(':scope > a.image-block, :scope > .image-block');
        if (!image) continue;

        var caption = null;
        var kids = content.children;
        for (var k = 0; k < kids.length; k++) {
          if (kids[k].tagName === 'A' && kids[k] !== image && kids[k].textContent.trim()) {
            caption = kids[k];
            break;
          }
        }
        if (!caption) continue;

        content.classList.add(CARD_CLASS);
        caption.classList.add(CARD_CLASS + '__caption');

        /* The tile around the content carries bottom padding that was there
           to leave room for the caption underneath. The caption is on the
           image now, so that space is a gap under the row - marked here so
           the stylesheet can close it without a :has() selector. */
        var tile = content.closest ? content.closest('.group-block') : null;
        if (tile) tile.classList.add(CARD_CLASS + '__tile');

        var name = caption.querySelector('.' + CARD_CLASS + '__name');
        if (!name) {
          name = document.createElement('span');
          name.className = CARD_CLASS + '__name';
          name.textContent = caption.textContent.trim();
          caption.textContent = '';
          caption.appendChild(name);
        }

        var total = counts[collectionHandle(caption)];
        var line = caption.querySelector('.' + CARD_CLASS + '__count');
        if (!total) {
          if (line) caption.removeChild(line);
          continue;
        }
        if (!line) {
          line = document.createElement('span');
          line.className = CARD_CLASS + '__count';
          caption.appendChild(line);
        }
        line.textContent = total + (total === '1' ? ' product' : ' products');
      }
    }
  }

  function restoreColumnScroll(node) {
    if (savedColumnScroll === null) return;
    var column = filterColumn(node);
    if (!column) return;
    column.scrollTop = savedColumnScroll;
    savedColumnScroll = null;
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  /* Reveal the "Show all" buttons only once JS is running, so a no-JS visitor
     never sees a control that cannot do anything. */
  function revealShowAllButtons(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll('[data-category-rail-show-all]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].hidden = false;
    }
  }

  function expandGroup(button) {
    /* Nearest list wins, so a Show all inside the style list expands only that. */
    var list = button.closest('.category-rail__styles, .category-rail__children');
    if (!list) return;

    revealOverflow(list);

    var group = button.closest('[data-category-rail-group]');
    if (group) rememberExpanded(group.getAttribute('data-category-key'));
  }

  document.addEventListener('click', function (event) {
    /* Category rail rows carry data-filter-name. Brand rail rows deliberately
       do not — those are navigation to another collection and must follow. */
    var filterRow = event.target.closest('a.category-rail__row[data-filter-name]');
    if (filterRow && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      applyFilterFromRow(filterRow, event);
      if (event.defaultPrevented) return;
    }

    var stale = event.target.closest('[data-category-rail-stale]');
    if (stale) {
      event.preventDefault();
      removeStaleSub(stale);
      return;
    }

    var groupSelect = event.target.closest('[data-category-rail-group-select]');
    if (groupSelect) {
      event.preventDefault();
      applyGroupSelect(groupSelect);
      return;
    }

    var showAll = event.target.closest('[data-category-rail-show-all]');
    if (showAll) {
      event.preventDefault();
      expandGroup(showAll);
      return;
    }

    /* Mobile: a category row with children opens the group rather than
       navigating. Desktop keeps the row as a plain link. */
    var toggle = event.target.closest('[data-category-rail-toggle]');
    if (toggle && isMobile()) {
      var targetId = toggle.getAttribute('data-category-rail-toggle');
      var panel = document.getElementById(targetId);
      if (!panel) return;

      var willOpen = panel.hidden;
      /* Already open means the buyer is asking for the category itself, so let
         the link through. */
      if (!willOpen) return;

      event.preventDefault();
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      revealShowAllButtons(panel);
    }
  });

  /* syncRail rather than the two helpers on their own: a page can be loaded
     directly on a URL that already carries a subcategory Shopify no longer
     reports, and that row has to be drawn on arrival, not only after the next
     filter change. */
  document.addEventListener('DOMContentLoaded', function () {
    syncRail();
  });

  /**
   * Re-apply everything that lives outside the server-rendered markup: the
   * Show all buttons, which categories were expanded, and the column's scroll
   * position.
   *
   * Deliberately re-run a few times rather than once. The mutation that tells
   * us the rail arrived can fire before the theme has finished swapping the
   * section in, and syncing against half-built markup silently does nothing —
   * which is exactly how an expanded category snapped shut on the next click.
   * Every step here is idempotent, so repeating it is harmless.
   */
  function syncRail() {
    addBandHeading();
    decorateBrandCards();
    revealShowAllButtons(document);
    applyExpandedState(document);

    var roots = document.querySelectorAll('[data-category-rail]');
    for (var i = 0; i < roots.length; i++) renderStaleSubs(roots[i]);

    if (roots.length) restoreColumnScroll(roots[0]);
  }

  /* The theme swaps the section in more than once and the last pass can land
     well after half a second, so the ladder has to outlast it rather than
     assume a single quick render. Timers are not cleared between calls: a
     later click simply adds more, and every step is idempotent. */
  var SYNC_DELAYS = [0, 60, 150, 300, 600, 1000, 1500, 2200];
  function scheduleSync() {
    syncRail();
    SYNC_DELAYS.forEach(function (delay) {
      setTimeout(syncRail, delay);
    });
  }

  /* Horizon swaps the filter markup in after a filter change. */
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (
          (node.matches && node.matches('[data-category-rail]')) ||
          (node.querySelector && node.querySelector('[data-category-rail]'))
        ) {
          /* No early return across the whole batch — a later record in the same
             batch can carry the markup that actually sticks. */
          scheduleSync();
          break;
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* The script is deferred, so the rail is usually already parsed by the time
     this runs and DOMContentLoaded may have been and gone. Both paths call the
     same idempotent sync. */
  syncRail();
})();
